import React from 'react';
import { ChartRenderer } from './ChartRenderer.js';
import { useChartData } from '../hooks/useChartData.js';

const CHART_TITLES = {
  grades: 'Grade Distribution',
  flash: 'Flash Probability',
  progression: 'Grade Progression',
  ascents: 'Ascent History',
  routesPerYear: 'Cumulative Routes',
  crags: 'Top Crags',
  types: 'Ascent Types',
  projects: 'Projects',
  cragPerformance: 'Crag Performance',
  bestClimbs: 'Best Climbs',
  partners: 'Climbing Partners',
  heatmap: 'Heatmap',
  cgpCge: 'CGP & CGE',
  cgpPerGrade: 'CGP/day by Grade'
};

const VALID_CHARTS = Object.keys(CHART_TITLES);

const SingleChartLayout = ({
  chart,
  filteredData,
  climbingStyle,
  gradeSystem,
  onGradeClick,
  chartLayouts,
  climberName,
  selectedYear,
  showHeader = true,
  headingOverride = null
}) => {
  // Compute chart-specific data for charts that need it (progression, types, crags)
  const chartData = useChartData(filteredData, chart, climbingStyle);

  if (!VALID_CHARTS.includes(chart)) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-red-600 mb-4">Invalid chart type: "{chart}"</p>
        <p className="text-gray-600">
          Valid options: {VALID_CHARTS.join(', ')}
        </p>
      </div>
    );
  }

  const defaultSettings = {
    showDistinctRoutes: true,
    selectedStat: 'cleanRoutes',
    numCrags: 30,
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {showHeader && (
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{headingOverride || CHART_TITLES[chart]}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {climberName && <span>{climberName}</span>}
              <span>{filteredData?.length?.toLocaleString() || 0} ascents</span>
              <span className="capitalize">{climbingStyle}</span>
              {selectedYear && selectedYear !== 'all' && <span>{selectedYear}</span>}
            </div>
          </div>
        </div>
      )}
      <div
        className="p-4"
        style={
          chart === 'bestClimbs'
            ? { minHeight: showHeader ? 'calc(100vh - 120px)' : '100vh' }
            : { height: showHeader ? 'calc(100vh - 120px)' : '100vh', minHeight: '400px' }
        }
      >
        <ChartRenderer
          selectedMetric={chart}
          chartData={chartData}
          filteredData={filteredData}
          climbingStyle={climbingStyle}
          gradeSystem={gradeSystem}
          onGradeClick={onGradeClick}
          isHorizontal={chartLayouts?.[chart] ?? false}
          showDistinctRoutes={defaultSettings.showDistinctRoutes}
          selectedStat={defaultSettings.selectedStat}
          numCrags={defaultSettings.numCrags}
        />
      </div>
    </div>
  );
};

export default SingleChartLayout;
